import { useCallback, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import {
  Text,
  Group,
  Stack,
  TextInput,
  Button,
  Textarea
} from 'components'
import { RichTextArea } from 'components'
import { useTranslation } from 'react-i18next'
import { IconPlus } from '@tabler/icons-react'
import { useProjectForm } from '../../hooks/useProjectForm'
import { useArcStepper } from '../../context/ArcStepperContext'
import { useArcGrades } from '../AboutStep/hooks'
import { GradeLevelSection } from '../AboutStep/components'
import { LearningGoalsList } from './components/LearningGoalsList'
import { ProjectCover } from 'pages/NewProject/components/ProjectCover'
import { CoverFileProvider } from 'pages/NewProject/context/CoverFileContext'
import { isBase64 } from 'pages/NewProject/components/ProjectDetails/helper'

export interface StructureStepRef {
  validateAndSave: () => boolean
}

export const StructureStep = forwardRef<StructureStepRef>((_, ref) => {
  const { t } = useTranslation()
  const { formData, errors, updateField, setErrors } = useProjectForm()
  const { projectId } = useArcStepper()
  const { grades, loading: gradesLoading } = useArcGrades()

  const [currentGoal, setCurrentGoal] = useState('')
  const isClickingAddButton = useRef(false)

  const handleCoverChange = useCallback((coverUrl: string | null) => {
    if (coverUrl && isBase64(coverUrl)) {
      updateField('coverImageUrl', coverUrl)
    } else if (!coverUrl) {
      updateField('coverImageUrl', null)
    }
  }, [updateField])

  const handleAddLearningGoal = useCallback(() => {
    if (!currentGoal.trim()) return

    const newGoals = [...formData.learningGoals, currentGoal.trim()]
    updateField('learningGoals', newGoals)
    setCurrentGoal('')
  }, [currentGoal, formData.learningGoals, updateField])

  const handleRemoveLearningGoal = useCallback((index: number) => {
    const newGoals = formData.learningGoals.filter((_, i) => i !== index)
    updateField('learningGoals', newGoals)
  }, [formData.learningGoals, updateField])

  const validateStep = useCallback(() => {
    const newErrors: Record<string, boolean> = {}

    if (!formData.gradeLevel) {
      newErrors.gradeLevel = true
    }

    if (!formData.projectName.trim()) {
      newErrors.projectName = true
    }

    if (!formData.drivingQuestion.trim()) {
      newErrors.drivingQuestion = true
    }

    if (!formData.summary.trim()) {
      newErrors.summary = true
    }

    if (formData.learningGoals.length === 0) {
      newErrors.learningGoals = true
    }

    const deliverableDoc = new DOMParser().parseFromString(formData.deliverable, 'text/html')
    const deliverableText = (deliverableDoc.body.textContent || '').replace(/\u00a0/g, ' ').trim()
    if (!deliverableText) {
      newErrors.deliverable = true
    }

    setErrors(newErrors)

    const fieldElements = document.querySelectorAll<HTMLElement>('[data-field]')
    for (const el of fieldElements) {
      const fieldName = el.getAttribute('data-field')
      if (fieldName && newErrors[fieldName]) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        break
      }
    }

    return Object.keys(newErrors).length === 0
  }, [formData, setErrors])

  useImperativeHandle(ref, () => ({
    validateAndSave: validateStep
  }), [validateStep])

  const showLearningGoalsError = !!errors.learningGoals && formData.learningGoals.length === 0

  return (
    <Stack spacing="md">
      <Group spacing={4}>
        <Text size="sm" color="red">*</Text>
        <Text fs="italic" size="lg" color="dimmed">
          {t('arc_required_fields')}
        </Text>
      </Group>

      <div data-field="gradeLevel">
        <GradeLevelSection
          gradeLevels={grades}
          value={formData.gradeLevel}
          onChange={(value) => updateField('gradeLevel', value)}
          loading={gradesLoading}
          error={!!errors.gradeLevel}
        />
      </div>

      <div data-field="projectName">
        <Text size="lg" weight={500} mb={0}>
          {t('arc_project_name_label')}
          <Text color="red" display="inline"> *</Text>
        </Text>
        <Text size="lg" color="dimmed" mb="sm">
          {t('arc_project_name_description')}
        </Text>
        <TextInput
          size="md"
          placeholder={t('arc_project_name_placeholder')}
          value={formData.projectName}
          onChange={(e) => updateField('projectName', e.currentTarget.value)}
          error={errors.projectName}
        />
        {errors.projectName && (
          <Text size="sm" color="red" mt={4}>
            {t('arc_field_required')}
          </Text>
        )}
      </div>

      <div data-field="drivingQuestion">
        <Text size="lg" weight={500} mb={0}>
          {t('arc_driving_question_label')}
          <Text color="red" display="inline"> *</Text>
        </Text>
        <Text size="lg" color="dimmed" mb="sm">
          {t('arc_driving_question_description')}
        </Text>
        <Textarea
          size="md"
          placeholder={t('arc_driving_question_placeholder')}
          value={formData.drivingQuestion}
          onChange={(e) => updateField('drivingQuestion', e.currentTarget.value)}
          error={errors.drivingQuestion}
          minRows={2}
        />
        {errors.drivingQuestion && (
          <Text size="sm" color="red" mt={4}>
            {t('arc_field_required')}
          </Text>
        )}
      </div>

      <div data-field="summary">
        <Text size="lg" weight={500} mb={0}>
          {t('arc_final_product_description_label')}
          <Text color="red" display="inline"> *</Text>
        </Text>
        <Text size="lg" color="dimmed" mb="sm">
          {t('arc_final_product_description_description')}
        </Text>
        <Textarea
          size="md"
          placeholder={t('arc_final_product_description_placeholder')}
          value={formData.summary}
          onChange={(e) => updateField('summary', e.currentTarget.value)}
          error={errors.summary}
          minRows={3}
        />
        {errors.summary && (
          <Text size="sm" color="red" mt={4}>
            {t('arc_field_required')}
          </Text>
        )}
      </div>

      <div data-field="learningGoals">
        <Text size="lg" weight={500} mb={0}>
          {t('arc_learning_goals_label')}
          <Text color="red" display="inline"> *</Text>
        </Text>
        <Text size="lg" color="dimmed" mb="sm">
          {t('arc_learning_goals_description')}
        </Text>
        <Group align="flex-end" spacing="xs" mb="sm">
          <TextInput
            size="md"
            placeholder={t('arc_learning_goal_placeholder')}
            value={currentGoal}
            onChange={(e) => setCurrentGoal(e.currentTarget.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddLearningGoal()}
            onBlur={() => {
              if (isClickingAddButton.current) return
              handleAddLearningGoal()
            }}
            style={{ flex: 1 }}
            error={showLearningGoalsError}
          />
          <Button
            size="md"
            variant="filled"
            color="teal"
            leftIcon={<IconPlus size={18} />}
            onMouseDown={() => { isClickingAddButton.current = true }}
            onClick={() => {
              handleAddLearningGoal()
              isClickingAddButton.current = false
            }}
            disabled={!currentGoal.trim()}
          >
            {t('arc_add_goal')}
          </Button>
        </Group>
        <LearningGoalsList
          goals={formData.learningGoals}
          onRemove={handleRemoveLearningGoal}
        />

        {showLearningGoalsError && (
          <Text size="sm" color="red" mt={4}>
            {t('arc_field_required')}
          </Text>
        )}
      </div>

      <div data-field="deliverable">
        <Text size="lg" weight={500} mb={0}>
          {t('arc_deliverable_label')}
          <Text color="red" display="inline"> *</Text>
        </Text>
        <Text size="lg" color="dimmed" mb="sm">
          {t('arc_deliverable_description')}
        </Text>
        <RichTextArea
          value={formData.deliverable}
          onChange={(value) => updateField('deliverable', value)}
          minHeight={150}
          error={!!errors.deliverable}
        />
        {errors.deliverable && (
          <Text size="sm" color="red" mt={4}>
            {t('arc_field_required')}
          </Text>
        )}
      </div>

      <div>
        <Text size="lg" weight={500} mb={0}>
          {t('arc_cover_image_label')}
        </Text>
        <Text size="lg" color="dimmed" mb="sm">
          {t('arc_cover_image_description')}
        </Text>
        <CoverFileProvider>
          <ProjectCover
            id={projectId || ''}
            onChange={handleCoverChange}
          />
        </CoverFileProvider>
      </div>
    </Stack>
  )
})

StructureStep.displayName = 'StructureStep'